import {CurrencyFormat, CurrencySettings} from 'accounting'

import {Region, validateRegion} from '@/utils/countries'

import {Locale} from './constants'

export const currencies = {
  EUR: 'EUR',
  CZK: 'CZK',
  GBP: 'GBP',
  USD: 'USD',
  AUD: 'AUD',
  CHF: 'CHF',
  CAD: 'CAD',
  NZD: 'NZD',
  MXN: 'MXN',
  ZAR: 'ZAR',
} as const

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- the value backs `typeof` below
const disabledCurrencies = {
  JPY: 'JPY',
  TRY: 'TRY',
} as const

export const zeroDecimalCurrencies = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]

export type Currency = keyof typeof currencies
export type DisabledCurrency = keyof typeof disabledCurrencies

export const regionToCurrency = (region: Region): Currency => {
  const regionCurrencyMap: Record<Region, Currency> = {
    AD: 'EUR',
    AE: 'USD',
    AR: 'USD',
    AU: 'AUD',
    AT: 'EUR',
    BD: 'USD',
    BX: 'EUR',
    BR: 'USD',
    BG: 'EUR',
    CA: 'CAD',
    CN: 'USD',
    CO: 'USD',
    HR: 'EUR',
    CZ: 'CZK',
    DK: 'EUR',
    EC: 'USD',
    EE: 'EUR',
    EG: 'USD',
    EU: 'EUR',
    FI: 'EUR',
    FR: 'EUR',
    GE: 'EUR',
    GB: 'GBP',
    DE: 'EUR',
    HK: 'USD',
    HU: 'EUR',
    IN: 'USD',
    ID: 'USD',
    IE: 'EUR',
    IL: 'USD',
    IT: 'EUR',
    JO: 'USD',
    JP: 'USD',
    KE: 'USD',
    KZ: 'USD',
    LB: 'USD',
    LV: 'EUR',
    LT: 'EUR',
    MA: 'USD',
    MC: 'EUR',
    MX: 'MXN',
    MY: 'USD',
    NZ: 'NZD',
    NG: 'USD',
    NO: 'EUR',
    PE: 'USD',
    PH: 'USD',
    PK: 'USD',
    PL: 'EUR',
    PT: 'EUR',
    RO: 'EUR',
    SA: 'USD',
    SG: 'USD',
    SK: 'EUR',
    SI: 'EUR',
    ZA: 'ZAR',
    KR: 'USD',
    ES: 'EUR',
    SE: 'EUR',
    CH: 'CHF',
    TW: 'USD',
    TH: 'USD',
    TN: 'USD',
    TR: 'USD',
    TZ: 'USD',
    UA: 'EUR',
    US: 'USD',
    VN: 'USD',
  }
  return regionCurrencyMap[region]
}

// The currency a jurisdiction is quoted in: its own where supported (GBP for UK), else USD/EUR.
export const jurisdictionToCurrency = (countryCode: string): Currency =>
  regionToCurrency(validateRegion(countryCode) ?? Region.US)

export const getCurrencySymbol = (currency: Currency | DisabledCurrency): string => {
  const currencySymbolMap: Record<Currency | DisabledCurrency, string> = {
    USD: '$',
    GBP: '£',
    CZK: 'Kč',
    AUD: 'AU$',
    EUR: '€',
    CHF: 'CHF',
    CAD: 'CA$',
    NZD: 'NZ$',
    TRY: '₺',
    JPY: '¥',
    MXN: 'Mex$',
    ZAR: 'R',
  }

  return currencySymbolMap[currency]
}

type CurrencyLabel = {name: string; icon: string}

export const getCurrencyLabel = (currency: Currency | DisabledCurrency): CurrencyLabel => {
  const currencyLabelMap: Record<Currency | DisabledCurrency, CurrencyLabel> = {
    USD: {
      name: 'page.currency-usd',
      icon: '/images/flags/us-flag.svg',
    },
    GBP: {
      name: 'page.currency-gbp',
      icon: '/images/flags/uk-flag.svg',
    },
    CZK: {
      name: 'page.currency-czk',
      icon: '/images/flags/cz-flag.svg',
    },
    AUD: {
      name: 'page.currency-aud',
      icon: '/images/flags/au-flag.svg',
    },
    EUR: {
      name: 'page.currency-eur',
      icon: '/images/flags/eu-flag.svg',
    },
    CHF: {
      name: 'page.currency-chf',
      icon: '/images/flags/ch-flag.svg',
    },
    CAD: {
      name: 'page.currency-cad',
      icon: '/images/flags/ca-flag.svg',
    },
    NZD: {
      name: 'page.currency-nzd',
      icon: '/images/flags/nz-flag.svg',
    },
    TRY: {
      name: 'page.currency-try',
      icon: '/images/flags/tr-flag.svg',
    },
    JPY: {
      name: 'page.currency-jpy',
      icon: '/images/flags/jp-flag.svg',
    },
    MXN: {
      name: 'page.currency-mxn',
      icon: '/images/flags/mx-flag.svg',
    },
    ZAR: {
      name: 'page.currency-zar',
      icon: '/images/flags/za-flag.svg',
    },
  }

  return currencyLabelMap[currency]
}

export const getThousandsSeparator = (locale: Locale): string => {
  return {
    [Locale.sk]: ' ',
    [Locale.cs]: ' ',
    [Locale.en]: ',',
    [Locale.es]: ' ',
    [Locale.de]: '.',
    [Locale.fr]: ' ',
    [Locale.it]: '.',
    [Locale.pl]: ' ',
    [Locale.pt]: ' ',
  }[locale]
}

export const getDecimalSeparator = (locale: Locale): string => {
  return {
    [Locale.sk]: ',',
    [Locale.cs]: ',',
    [Locale.en]: '.',
    [Locale.es]: ',',
    [Locale.de]: ',',
    [Locale.fr]: ',',
    [Locale.it]: ',',
    [Locale.pl]: ',',
    [Locale.pt]: ',',
  }[locale]
}

export const getFormattingOptions = (
  currency: Currency,
  language: Locale,
  hasDecimal = true,
  useCurrencyCode = false,
): CurrencySettings<CurrencyFormat> => ({
  symbol: useCurrencyCode ? currency : getCurrencySymbol(currency),
  decimal: getDecimalSeparator(language),
  thousand: getThousandsSeparator(language),
  format:
    !useCurrencyCode &&
    (([currencies.GBP, currencies.USD, currencies.AUD, currencies.CAD] as Currency[]).includes(
      currency,
    ) ||
      Locale.en === language)
      ? getCurrencySymbol(currency)?.length > 1
        ? {pos: '%s %v', neg: '- %s %v', zero: '%s %v'}
        : {pos: '%s%v', neg: '- %s%v', zero: '%s%v'}
      : {pos: '%v %s', neg: '- %v %s', zero: '%v %s'},
  precision: hasDecimal ? 2 : 0,
})

export const getCurrencySelectChoices = () => {
  return Object.keys(currencies).map((key: string) => ({
    id: key,
    name: key,
  }))
}
